import { ArrayUtils } from '@shopping/common'
import { StateKey } from '../../utils/flipState'
import { AiTemsResult } from '../../types/entities/api/aiTems'
import { AiTemsResponse } from 'common/types/entities/api/response'
import HttpStatus from 'common/utils/httpStatus'
import { fetchZzimedProducts, setProductZzim } from 'common/services/zzim'
import { Error } from 'common/types/entities/error'
import { patchZzim, putZzim } from 'common/ducks/product/zzim'
import { WindowProduct } from 'common/types/entities/mongo/product'
import { fetchProductsByProductParam } from 'common/services/list'
import { ProductParam } from 'common/types/params/list'
import { SubVertical } from 'common/constants'
import history from 'common/history'
import { axios } from 'common/utils'
import { getHeaders } from 'common/utils/ajax/axios'

export interface AiTemsState extends AiTemsResult {
  page: number
  pageSize: number
  windowProducts: GroupedWindowProduct[]
  infiniteGridStatus: any
}

interface GroupedWindowProduct extends WindowProduct {
  groupKey: number
}

const initialAiTems: AiTemsResult = {
  titleTemplate: null,
  tgAl: null,
  themeIdx: null,
  themeTitle: null,
  themes: null,
  products: null,
}

const initialState: AiTemsState = {
  ...initialAiTems,
  page: 1,
  pageSize: 30,
  windowProducts: [],
  infiniteGridStatus: null,
}

const FETCH = '/product/aitems/FETCH'
const RESTORE_FIRST_PAGE = '/product/aitems/RESTORE_FIRST_PAGE'
const SET_PAGE = '/product/aitems/SET_PAGE'
const SET_PRODUCT_ZZIM = '/product/aitems/SET_PRODUCT_ZZIM'

export const fetchAiTems = (
  curState: StateKey,
  subVertical: SubVertical,
  nvmid: string,
  pageSize: number,
  theme?: number,
  headers?: any,
) => async dispatch => {
  const restoredState = getRestoredStateFromHistory()
  if (restoredState) {
    dispatch({
      curState,
      aiTemsState: restoredState,
      type: FETCH,
    })
    return
  }

  const response = await axios.get<AiTemsResponse<AiTemsResult>>(`/v1/aitems/${nvmid}`, {
    ...getHeaders(headers),
    params: {
      theme,
    },
  })

  const aiTemsResult: AiTemsResult = (response && response.data && response.data.result) || initialAiTems
  const windowProducts: GroupedWindowProduct[] = []
  if (aiTemsResult.products) {
    const productIds = aiTemsResult.products.slice(0, pageSize).map(product => product.mallPid)

    const mapProducts = {}
    const fetchedProducts = await fetchWindowProducts(dispatch, subVertical, productIds)
    fetchedProducts.forEach(product => (mapProducts[product._id] = product))

    productIds.forEach(id => mapProducts[id] && windowProducts.push({ ...mapProducts[id], groupKey: 1 }))
  }

  const aiTemsState = {
    ...aiTemsResult,
    windowProducts,
    pageSize,
    page: 1,
    infiniteGridStatus: null,
  }

  dispatch({
    curState,
    aiTemsState,
    type: FETCH,
  })
}

export const restoreFirstPage = (curState: StateKey) => (dispatch, getState) => {
  const state: AiTemsState = getState().aiTems[curState]
  const { windowProducts, pageSize } = state
  if (ArrayUtils.isEmpty(windowProducts)) return

  const aiTemsState = {
    page: 1,
    windowProducts: windowProducts.slice(0, pageSize),
    infiniteGridStatus: null,
  }

  dispatch({
    curState,
    aiTemsState,
    type: RESTORE_FIRST_PAGE,
  })
}

const getRestoredStateFromHistory = (): AiTemsState => {
  if (history.location && history.location.state && history.location.state.aiTemsState) {
    const state = history.location.state.aiTemsState
    history.location.state.aiTemsState = null
    return state
  }
  return null
}

export const increasePage = (curState: StateKey, subVertical: SubVertical) => async (dispatch, getState) => {
  const state: AiTemsState = getState().aiTems[curState]
  const { page, pageSize, products } = state

  const nextPage = page + 1
  const begin = page * pageSize
  const end = Math.min(nextPage * pageSize, products.length)

  const productIds = products.length > begin ? products.slice(begin, end).map(p => p.mallPid) : []
  const windowProducts = await fetchWindowProducts(dispatch, subVertical, productIds)
  windowProducts.forEach(product => (product.groupKey = nextPage))

  dispatch({
    curState,
    windowProducts,
    pageSize,
    page: nextPage,
    type: SET_PAGE,
  })
}

export const toggleProductZzim = (curState: StateKey, productId: string, zzimed: boolean) => async dispatch => {
  const responseData = await setProductZzim(productId, zzimed)
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: responseData.htReturnValue.sMessage,
    }
    throw error
  }

  await dispatch(patchZzim(curState, productId, !zzimed))
  dispatch({
    curState,
    productId,
    type: SET_PRODUCT_ZZIM,
  })
}

const fetchWindowProducts = async (
  dispatch,
  subVertical: SubVertical,
  productIds: string[],
): Promise<WindowProduct[]> => {
  const params: ProductParam = {
    subVertical,
    productIds,
    page: 1,
    pageSize: productIds.length,
    includeStoreCardInfo: true,
  }
  const storeProducts = await fetchProductsByProductParam(params)
  const windowProducts = (storeProducts && storeProducts.products) || []

  if (ArrayUtils.isNotEmpty(windowProducts)) {
    const zzimProducts = await fetchZzimedProducts(productIds)
    windowProducts.forEach(product => {
      const zzim = zzimProducts[product._id]
      product.zzimCount = zzim && zzim.count
      product.isMyZzim = (zzim && zzim.zzim) || false
    })
  }

  return windowProducts
}

const updateProductZzim = (products: GroupedWindowProduct[], productId: string) => {
  const index = products.findIndex(product => product._id === productId)
  const windowProduct = products[index]
  windowProduct.isMyZzim = !windowProduct.isMyZzim

  return [...products.slice(0, index), windowProduct, ...products.slice(index + 1)]
}

// reducers
const aiTemsReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH:
    case RESTORE_FIRST_PAGE:
      return {
        ...state,
        ...action.aiTemsState,
      }
    case SET_PAGE:
      return {
        ...state,
        windowProducts: [...state.windowProducts, ...action.windowProducts],
        page: action.page,
        pageSize: action.pageSize,
        infiniteGridStatus: null,
      }
    case SET_PRODUCT_ZZIM:
      return {
        ...state,
        products: updateProductZzim(state.windowProducts, action.productId),
      }
    default:
      return state
  }
}

export default aiTemsReducer
